import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api, whatsappLink } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";
import { useLang } from "@/i18n/LanguageContext";
import SavingsCalculator from "@/components/SavingsCalculator";
import ClinicCard from "@/components/ClinicCard";
import { Star, ShieldCheck, MessageCircle, ArrowRight, Plane, ClipboardCheck, Stethoscope, HeartHandshake, Quote, Car, BedDouble, Languages, Headset, Phone, BadgeCheck, Mail, PlayCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import TransformationCard from "@/components/TransformationCard";
import { TRANSFORMATIONS } from "@/data/transformations";
import { LOCATIONS } from "@/data/locations";
import { PHONE } from "@/lib/api";

const HERO_IMG = "/team/brandon-drtorres.webp";

const VETTED = [
  { name: "Dentology", location: "Cancún, Mexico", img: "/vetting/dentology.webp" },
  { name: "Marietta Dental Care", location: "Tijuana, Mexico", img: "/vetting/marietta.webp" },
  { name: "Rumo Dental Group", location: "Los Algodones, Mexico", img: "/vetting/rumo.webp" },
  { name: "Art Dental Care", location: "San José, Costa Rica", img: "/vetting/art-dental.webp" },
  { name: "Supreme Dental", location: "Los Algodones, Mexico", img: "/vetting/supreme-podcast.webp" },
  { name: "Dentica Dental Clinic", location: "Bogotá, Colombia", img: "/vetting/dentica.webp" },
  { name: "Dental Brush", location: "Tijuana, Mexico", img: "/vetting/dental-brush.webp" },
  { name: "Aesthetic Smiles Studio", location: "Tijuana, Mexico", img: "/vetting/aesthetic-smiles.webp" },
];

const GUARANTEE_ICONS = [ShieldCheck, BadgeCheck, HeartHandshake];
const CONCIERGE_ICONS = [Plane, Car, BedDouble, Languages];
const STEP_ICONS = [ClipboardCheck, Stethoscope, Plane, HeartHandshake];

const US_PINS = [
  { name: "San Diego", x: 13.3, y: 66.5 },
  { name: "Palm Springs", x: 15.0, y: 62.5 },
  { name: "Calexico", x: 16.2, y: 67.5 },
  { name: "Yuma", x: 18.0, y: 67.0 },
  { name: "Phoenix", x: 22.5, y: 63.5 },
  { name: "McAllen, TX", x: 45.5, y: 90.0 },
  { name: "Weslaco", x: 47.0, y: 91.0 },
];
const SHUTTLE_STOPS = [
  "San Diego", "Palm Springs", "Calexico", "Yuma", "Phoenix",
  "McAllen, TX", "Weslaco", "Cancún", "San José, CR", "Bogotá", "Istanbul",
];

const CERTS = [
  { src: "/certs/harvard.png", alt: "Harvard" },
  { src: "/certs/penn.png", alt: "Penn State University" },
  { src: "/certs/ada.png", alt: "American Dental Association" },
  { src: "/certs/iti.jpg", alt: "ITI" },
  { src: "/certs/agd.jpg", alt: "Academy of General Dentistry" },
  { src: "/certs/aaid.png", alt: "AAID" },
];

const LOCATION_ORDER = [
  { key: "tijuana", match: (c) => c.city === "Tijuana", order: ["Dental Brush Tijuana", "Marietta Dental Care Tijuana", "Aesthetic Smiles Studio"] },
  { key: "mexicali", match: (c) => c.city === "Mexicali" },
  { key: "algodones", match: (c) => c.city === "Los Algodones", order: ["Supreme Dental", "Rumo Dental Group", "Good Smile Dental Clinic", "Marietta Dental Care"] },
  { key: "cancun", match: (c) => c.city === "Cancún" },
  { key: "costarica", match: (c) => c.country === "Costa Rica" },
  { key: "colombia", match: (c) => c.country === "Colombia" },
  { key: "turkey", match: (c) => c.country === "Turkey" },
  { key: "progreso", match: (c) => c.city === "Nuevo Progreso" },
];

const HOME_TRANSFORMS = (() => {
  const featured = TRANSFORMATIONS.filter((t) => t.featured);
  const norm = (c) => (c || "").replace(/\s+Tijuana$/, "").trim();
  const seen = new Set();
  const first = [];
  const rest = [];
  featured.forEach((t) => {
    const k = norm(t.clinic);
    if (k && !seen.has(k)) { seen.add(k); first.push(t); } else rest.push(t);
  });
  return [...first, ...rest];
})();

let _homeCache = { clinics: [], testimonials: [], posts: [] };

export default function Home() {
  const { openQuote } = useQuote();
  const { t } = useLang();
  const [clinics, setClinics] = useState(_homeCache.clinics);
  const [testimonials, setTestimonials] = useState(_homeCache.testimonials);

  useEffect(() => {
    api.get("/clinics").then((r) => { _homeCache.clinics = r.data; setClinics(r.data); }).catch(() => {});
    api.get("/testimonials").then((r) => { _homeCache.testimonials = r.data; setTestimonials(r.data); }).catch(() => {});
    api.get("/blog").then((r) => { _homeCache.posts = r.data.slice(0, 3); }).catch(() => {});
  }, []);

  const concierge = t("home.concierge");
  const steps = t("home.steps");
  const faqs = t("home.faqs");
  const guarantees = t("home.guarantees");
  const compareRows = t("home.compareRows");

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-8 lg:pt-20 lg:pb-16 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent/10 text-accent px-3 py-1 text-xs font-semibold tracking-wide">
              <ShieldCheck className="w-3.5 h-3.5" /> {t("home.heroBadge")}
            </span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-secondary">
              {t("home.heroTitle1")}<br /><span className="text-primary">{t("home.heroTitle2")}</span>
            </h1>
            <p className="text-lg text-muted-foreground mt-5 max-w-md" dangerouslySetInnerHTML={{ __html: t("home.heroSub") }} />
            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <Button onClick={() => openQuote({ type: "quote" })} className="rounded-full h-12 px-7 text-base" data-testid="hero-get-quote-btn">
                {t("common.getFreeQuote")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a href={`tel:${PHONE.replace(/[^+\d]/g, "")}`}>
                <Button variant="outline" className="rounded-full h-12 px-7 text-base w-full border-primary text-primary hover:bg-primary hover:text-white" data-testid="hero-call-btn">
                  <Phone className="w-4 h-4 mr-2" /> {t("common.callUsNow")}
                </Button>
              </a>
            </div>
            <div className="flex items-center gap-6 mt-8">
              <div><p className="font-heading font-extrabold text-2xl text-secondary">4.9★</p><p className="text-xs text-muted-foreground">{t("home.statAvg")}</p></div>
              <div className="w-px h-10 bg-border" />
              <div><p className="font-heading font-extrabold text-2xl text-secondary">16k+</p><p className="text-xs text-muted-foreground">{t("home.statPatients")}</p></div>
              <div className="w-px h-10 bg-border" />
              <div><p className="font-heading font-extrabold text-2xl text-secondary">4</p><p className="text-xs text-muted-foreground">{t("home.statCountries")}</p></div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-primary/10 rounded-[2rem] blur-2xl" />
            <img src={HERO_IMG} alt="Happy dental patient" className="relative rounded-[2rem] shadow-2xl w-full object-cover aspect-[4/5] max-h-[520px]" />
            <div className="absolute -bottom-5 -left-2 sm:left-4 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 animate-float">
              <div className="w-11 h-11 rounded-full bg-accent/10 flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-accent" /></div>
              <div><p className="text-sm font-semibold leading-none">{t("home.guaranteeBadge")}</p><p className="text-xs text-muted-foreground mt-1">{t("home.guaranteeBadgeSub")}</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS STRIP */}
      <section className="border-y border-border bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-6">{t("home.certsTitle")}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
            {CERTS.map((c) => (
              <img key={c.alt} src={c.src} alt={c.alt} className="h-12 sm:h-16 w-auto object-contain transition-transform duration-200 hover:scale-110" />
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6" dangerouslySetInnerHTML={{ __html: t("home.certsFootnote") }} />
        </div>
      </section>

      {/* SAVINGS CALCULATOR */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <SavingsCalculator />
      </section>

      {/* OUR CLINICS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-4 pt-10">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("home.clinicsEyebrow")}</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">{t("home.clinicsTitle")}</h2>
          <p className="text-muted-foreground mt-3">{t("home.clinicsSub")}</p>
        </div>
        <div className="max-w-5xl mx-auto space-y-12">
          {LOCATION_ORDER.map((loc) => {
            let group = clinics.filter(loc.match);
            if (!group.length) return null;
            if (loc.order) {
              const rank = (n) => { const i = loc.order.indexOf(n); return i === -1 ? 999 : i; };
              group = [...group].sort((a, b) => rank(a.name) - rank(b.name));
            }
            return (
              <div key={loc.key} data-testid={`clinic-group-${loc.key}`}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-5 border-b border-border pb-3">
                  <h3 className="font-heading text-xl font-bold text-secondary">{t(`home.locations.${loc.key}.title`)}</h3>
                  <span className="text-sm text-muted-foreground">{t(`home.locations.${loc.key}.sub`)}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  {group.map((c, i) => <ClinicCard key={c.id} clinic={c} index={i} />)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-8">
          <Link to="/clinics"><Button variant="outline" className="rounded-full px-6" data-testid="home-browse-clinics">{t("home.browseClinics")}</Button></Link>
        </div>
      </section>

      {/* BEFORE & AFTER + patient videos */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("home.transformsEyebrow")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">{t("home.transformsTitle")}</h2>
            <p className="text-muted-foreground mt-3">{t("home.transformsSub")} <span className="text-foreground/70">{t("home.swipeMore")}</span></p>
          </div>

          <div className="flex gap-4 sm:gap-6 overflow-x-auto pb-4 snap-x -mx-4 px-4 sm:-mx-6 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="home-transformations">
            {HOME_TRANSFORMS.map((item) => (
              <TransformationCard key={item.id} item={item} className="min-w-[260px] w-[260px] sm:min-w-[300px] sm:w-[300px] shrink-0 snap-start !mb-0" />
            ))}
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <Link to="/video-testimonials">
              <Button variant="outline" className="rounded-full px-7 h-12 border-primary text-primary hover:bg-primary hover:text-white" data-testid="home-video-testimonials-link">
                <PlayCircle className="w-4 h-4 mr-2" /> {t("home.seeVideos")}
              </Button>
            </Link>
            <Button onClick={() => openQuote({ type: "quote" })} className="rounded-full px-7 h-12" data-testid="transformations-quote-cta">
              {t("home.seeCandidate")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-3">{t("home.candidateNote")}</p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="patient-stories" className="bg-secondary/5 py-16" data-testid="home-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("home.testimonialsEyebrow")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">{t("home.testimonialsTitle")}</h2>
          </div>

          <div className="grid lg:grid-cols-5 gap-6 items-start">
            <div className="lg:col-span-2">
              <div className="rounded-2xl bg-white border border-border shadow-sm overflow-hidden" data-testid="featured-video-testimonial">
                <div className="mx-auto w-full max-w-[320px]">
                  <video src="/clinics/marietta-alg/sara-testimonial.mp4" controls playsInline preload="metadata" poster="/clinics/marietta-alg/sara-poster.jpg" className="w-full aspect-[9/16] object-cover bg-black" />
                </div>
                <div className="p-5">
                  <p className="font-heading font-bold text-lg leading-snug text-secondary">{t("home.saraQuote")}</p>
                  <div className="flex items-center gap-1.5 mt-3">
                    <BadgeCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                    <p className="text-sm text-foreground"><b>{t("home.saraName")}</b> · {t("home.saraProcedure")}</p>
                  </div>
                  <p className="text-xs text-emerald-700 font-semibold mt-1.5">{t("home.saraVerified")}</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
              {testimonials.map((tm, i) => (
                <div key={tm.id} className="flex flex-col rounded-2xl bg-white border border-border p-6 shadow-sm animate-fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`testimonial-${tm.id}`}>
                  <Quote className="w-8 h-8 text-primary/20" />
                  <div className="flex gap-0.5 my-3">{Array.from({ length: tm.rating }).map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                  <p className="text-sm text-foreground/90 leading-relaxed flex-1">"{tm.quote}"</p>
                  <div className="flex items-center gap-3 mt-5 pt-5 border-t border-border">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm">{tm.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{tm.location} · {tm.procedure}</p>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 mt-1">
                        <BadgeCheck className="w-3.5 h-3.5" /> {t("common.verifiedPatient")}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              <div className="sm:col-span-2 rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-sm text-foreground/80"><b>{t("home.shareBold")}</b> {t("home.shareRest")}</p>
                <Link to="/share-your-story" className="shrink-0"><Button className="rounded-full px-6" data-testid="share-story-btn">{t("home.shareStory")}</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HUMAN / NO BOTS TRUST CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12" data-testid="home-human-cta">
        <div className="rounded-3xl border border-accent/25 bg-accent/[0.06] p-6 sm:p-10 flex flex-col md:flex-row items-center gap-6 md:gap-10">
          <div className="shrink-0 flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-accent/15 text-accent">
            <Headset className="w-8 h-8 sm:w-10 sm:h-10" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent bg-accent/10 rounded-full px-3 py-1 mb-3">
              <ShieldCheck className="w-3.5 h-3.5" /> {t("home.humanBadge")}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-foreground">{t("home.humanTitle")}</h2>
            <p className="text-muted-foreground mt-2 max-w-xl mx-auto md:mx-0">{t("home.humanSub")}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <Button onClick={() => openQuote({ type: "quote" })} className="rounded-full h-12 px-7 bg-accent hover:bg-accent/90" data-testid="home-human-quote-btn">{t("home.humanQuoteBtn")}</Button>
            <a href={`tel:${PHONE}`} className="w-full sm:w-auto" data-testid="home-human-call-link">
              <Button variant="outline" className="rounded-full h-12 px-7 w-full border-accent text-accent hover:bg-accent hover:text-white"><Phone className="w-4 h-4 mr-2" /> {t("home.humanCallBtn")}</Button>
            </a>
          </div>
        </div>
      </section>

      {/* CONCIERGE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="rounded-3xl bg-secondary text-white p-6 sm:p-10 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, white 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">
                <Plane className="w-3.5 h-3.5" /> {t("home.conciergeBadge")}
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold mt-4 leading-tight">{t("home.conciergeTitle1")}<br /><span className="text-accent">{t("home.conciergeTitle2")}</span></h2>
              <p className="text-white/70 mt-3 max-w-md">{t("home.conciergeSub")}</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button onClick={() => openQuote({ type: "call" })} className="rounded-full h-12 px-7 bg-accent hover:bg-accent/90" data-testid="concierge-talk-btn">
                  <Headset className="w-4 h-4 mr-2" /> {t("home.conciergeTalk")}
                </Button>
                <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="rounded-full h-12 px-7 w-full bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary" data-testid="concierge-whatsapp-btn">
                    <MessageCircle className="w-4 h-4 mr-2" /> {t("common.whatsappUs")}
                  </Button>
                </a>
              </div>
              <Link to="/journey" className="inline-flex items-center gap-1 text-sm text-accent hover:underline mt-4 font-medium" data-testid="concierge-journey-link">{t("home.conciergeJourney")} <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {concierge.map((c, i) => {
                const Icon = CONCIERGE_ICONS[i];
                return (
                  <div key={i} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                    <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center mb-2.5"><Icon className="w-5 h-5 text-accent" /></div>
                    <h3 className="font-heading font-bold text-sm">{c.title}</h3>
                    <p className="text-sm text-white/60 mt-1">{c.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="relative mt-8 pt-6 border-t border-white/10" data-testid="shuttle-locations">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center shrink-0"><Car className="w-5 h-5 text-accent" /></div>
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 text-accent px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide mb-1">{t("home.shuttleBadge")}</span>
                <h3 className="font-heading font-bold text-lg leading-tight">{t("home.shuttleTitle")}</h3>
                <p className="text-sm text-white/60 mt-1">{t("home.shuttleSub")}</p>
              </div>
            </div>

            <div className="mb-6 rounded-xl bg-accent/10 border border-accent/25 px-4 py-3 flex items-start gap-2.5" data-testid="fly-domestic-note">
              <Plane className="w-4 h-4 text-accent mt-0.5 shrink-0" />
              <p className="text-sm text-white/90"><b className="text-white">{t("home.flyDomesticBold")}</b> {t("home.flyDomesticRest")}</p>
            </div>

            <div className="grid lg:grid-cols-5 gap-6 items-center">
              <button type="button" onClick={() => openQuote({ type: "quote" })} className="group/map hidden lg:block lg:col-span-2 relative rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden text-left transition-colors hover:border-accent/50" data-testid="shuttle-map">
                <div className="relative w-full aspect-[1536/1024]">
                  <img src="/assets/us-map-dotted.png" alt="US map of My Global Dentist shuttle pickup locations" className="absolute inset-0 w-full h-full object-contain opacity-70" />
                  {US_PINS.map((s) => (
                    <span key={s.name} className="group/pin absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${s.x}%`, top: `${s.y}%` }} data-testid={`shuttle-pin-${s.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                      <span className="absolute inset-0 -m-1.5 rounded-full bg-accent/40 animate-ping" />
                      <span className="relative block w-2.5 h-2.5 rounded-full bg-accent ring-2 ring-white/70 shadow" />
                      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-6 whitespace-nowrap rounded-md bg-secondary/95 border border-white/15 px-1.5 py-0.5 text-[10px] font-semibold text-white opacity-0 group-hover/pin:opacity-100 transition-opacity">{s.name}</span>
                    </span>
                  ))}
                </div>
                <span className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2 text-sm font-semibold text-white shadow-lg transition-transform group-hover/map:scale-105" data-testid="shuttle-map-cta">
                  <Car className="w-4 h-4" /> {t("home.arrangeShuttle")} <ArrowRight className="w-4 h-4" />
                </span>
              </button>

              <div className="lg:col-span-3">
                <button type="button" onClick={() => openQuote({ type: "quote" })} className="lg:hidden w-full mb-5 inline-flex items-center justify-center gap-2 rounded-full bg-accent px-4 py-3 text-sm font-semibold text-white shadow-lg transition-transform active:scale-95" data-testid="shuttle-mobile-cta">
                  <Car className="w-4 h-4" /> {t("home.arrangeShuttle")} <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-xs uppercase tracking-[0.15em] text-white/40 font-semibold mb-3">{t("home.allPickups")}</p>
                <div className="flex flex-wrap gap-2">
                  {SHUTTLE_STOPS.map((name) => (
                    <span key={name} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/10 px-3 py-1.5 text-xs font-medium text-white/90" data-testid={`shuttle-stop-${name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-accent" /> {name}
                    </span>
                  ))}
                </div>
                <button type="button" onClick={() => openQuote({ type: "call" })} className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline font-medium mt-4" data-testid="shuttle-no-city-cta">
                  {t("home.noCity")} <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VETTED CLINICS */}
      <section className="bg-secondary/5 py-16" data-testid="vetted-clinics">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("home.vettedEyebrow")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">{t("home.vettedTitle")}</h2>
            <p className="text-muted-foreground mt-3">{t("home.vettedSub")}</p>
          </div>
          <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 snap-x -mx-4 px-4 sm:-mx-6 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {VETTED.map((v) => (
              <figure key={v.name} className="min-w-[160px] w-[160px] sm:min-w-[180px] sm:w-[180px] shrink-0 snap-start rounded-2xl overflow-hidden bg-white border border-border shadow-sm" data-testid={`vetted-${v.name.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="aspect-[4/3] overflow-hidden bg-secondary/10 flex items-center justify-center">
                  <img src={v.img} alt={t("home.vettedVisiting", { name: v.name })} loading="lazy" className="w-full h-full object-contain" />
                </div>
                <figcaption className="px-3 py-2.5 border-t border-border">
                  <p className="font-heading font-bold text-xs leading-tight truncate">{v.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{v.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6 flex items-center justify-center gap-1.5"><ShieldCheck className="w-4 h-4 text-accent" /> {t("home.vettedFootnote")}</p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center max-w-2xl mx-auto mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("home.howEyebrow")}</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">{t("home.howTitle")}</h2>
          <p className="text-muted-foreground mt-2">{t("home.howSub")}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <div key={i} className="relative rounded-2xl border border-border bg-white p-5 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <span className="absolute top-4 right-4 font-heading font-extrabold text-3xl text-primary/10">{i + 1}</span>
                <div className="w-11 h-11 rounded-xl icon-chip flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-primary" /></div>
                <h3 className="font-heading font-bold text-base">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FOUNDER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2">
            <div className="relative">
              <div className="absolute -inset-3 bg-primary/10 rounded-[2rem] blur-xl" />
              <img src="/team/daniel.webp" alt="Daniel, Founder of My Global Dentist" className="relative rounded-[2rem] shadow-xl w-full object-cover aspect-[4/5] max-w-sm mx-auto" data-testid="founder-photo" />
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("home.founderEyebrow")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2 text-secondary">{t("home.founderTitle")}</h2>
            <div className="mt-5 space-y-4 text-foreground/80 leading-relaxed">
              <p>{t("home.founderP1")}</p>
              <p dangerouslySetInnerHTML={{ __html: t("home.founderP2") }} />
              <p>{t("home.founderP3")}</p>
            </div>
            <div className="mt-6">
              <p className="font-heading font-bold text-lg text-secondary">Daniel Villalta</p>
              <p className="text-sm text-muted-foreground">{t("home.founderRole")}</p>
              <a href="mailto:daniel@myglobaldentist.net" className="mt-3 inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-primary transition-colors" data-testid="founder-email-link">
                <Mail className="w-4 h-4 text-primary" /> {t("home.founderReach")} <span className="font-medium text-primary">daniel@myglobaldentist.net</span>
              </a>
              <p className="text-xs text-muted-foreground mt-1.5 italic">{t("home.founderReadMsg")}</p>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-6">
              <Button onClick={() => openQuote({ type: "call" })} className="rounded-full h-12 px-7" data-testid="founder-cta-btn">{t("home.founderTalk")}</Button>
              <Link to="/blog/welcome-from-our-founder" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all" data-testid="founder-read-full-link">
                {t("home.founderReadFull")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("home.destEyebrow")}</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-1">{t("home.destTitle")}</h2>
          </div>
          <Link to="/locations"><Button variant="ghost" className="rounded-full" data-testid="home-view-all-locations">{t("home.destAll")}</Button></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {LOCATIONS.slice(0, 4).map((l, i) => (
            <Link key={l.slug} to={`/locations/${l.slug}`} className="group relative rounded-2xl overflow-hidden h-44 animate-fade-up" style={{ animationDelay: `${i * 70}ms` }} data-testid={`home-location-${l.slug}`}>
              <img src={l.hero} alt={l.city} className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-4 text-white">
                <p className="text-[11px] text-white/70">{l.country}</p>
                <h3 className="font-heading font-bold text-lg leading-tight">{l.city}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SAVINGS COMPARISON */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="savings-comparison">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("home.mathEyebrow")}</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">{t("home.mathTitle")}</h2>
          <p className="text-muted-foreground mt-3">{t("home.mathSub")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {compareRows.map((c) => (
            <div key={c.t} className="relative rounded-2xl bg-white border border-border p-6 shadow-sm hover:shadow-md transition-shadow" data-testid={`compare-${c.t}`}>
              <span className="absolute top-4 right-4 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold px-2.5 py-1">{t("home.saveUpTo", { save: c.save })}</span>
              <p className="font-heading font-bold text-base pr-20">{c.t}</p>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="text-sm text-muted-foreground">{t("home.inUsa")}</span>
                <span className="text-lg text-muted-foreground line-through decoration-red-400">{c.us}</span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-sm text-foreground/70">{t("home.withUsFrom")}</span>
                <span className="font-heading text-3xl font-extrabold text-primary">{c.ours}</span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-4">{t("home.mathFootnote")}</p>

        <div className="mt-10 rounded-2xl bg-secondary text-white p-6 sm:p-8 grid sm:grid-cols-3 gap-6">
          {guarantees.map((g, i) => {
            const Icon = GUARANTEE_ICONS[i];
            return (
              <div key={i} className="flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                <div>
                  <p className="font-semibold">{g.t}</p>
                  <p className="text-sm text-white/70 mt-1">{g.d}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-8">
          <Button onClick={() => openQuote({ type: "quote" })} className="rounded-full h-12 px-8 text-base bg-accent hover:bg-accent/90" data-testid="savings-quote-cta">
            {t("home.getWrittenQuote")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-xs text-muted-foreground mt-3">{t("common.replyWithin2h")}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t("home.faqEyebrow")}</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">{t("home.faqTitle")}</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-2xl px-5 bg-white" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-heading font-semibold hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="rounded-3xl bg-secondary text-white p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold">{t("home.finalTitle")}</h2>
            <p className="text-white/70 mt-3 max-w-lg mx-auto">{t("home.finalSub")}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
              <Button onClick={() => openQuote({ type: "quote" })} className="rounded-full h-12 px-8 bg-accent hover:bg-accent/90" data-testid="cta-get-quote-btn">{t("common.getFreeQuote")}</Button>
              <Button onClick={() => openQuote({ type: "call" })} variant="outline" className="rounded-full h-12 px-8 bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary" data-testid="cta-book-call-btn">{t("common.bookCall")}</Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
